import type { LucideIcon } from 'lucide-react';
import { Heart, Palette, Pipette, Snowflake, Sun } from 'lucide-react';
import type { AceAvatarFrame } from '@/lib/ace-avatar-frames';
import type { PowerPstyleCmsConfig } from '@/lib/power-pstyle-themes';
import type { CmsPublicationStatus } from '@/lib/cms-publication';
import type { RelatedExploreConfig } from '@/lib/related-explore';
import { resolveRelatedByItems } from '@/lib/related-explore';

/** Escopo de aplicação do Ace (todos os Aces atuais são de conta). */
export type AceScope = 'account' | 'chat';

export type AceBadgeId = 'conta' | 'chat' | 'niveis' | 'renovacao' | 'novo';

export type AceStatus = 'active' | 'legacy' | 'upcoming';

export type AceFilterId = 'all' | 'account' | 'chat' | 'with-levels' | 'without-levels';

export type AceSortId = 'recent' | 'name' | 'popularity';

export type AceLevel = {
  level: number;
  name: string;
  description: string;
  perks: string[];
};

export type AceFeature = {
  id: string;
  title: string;
  description: string;
};

export type AceHowItWorksStep = {
  step: number;
  title: string;
  description: string;
};

export type AceFaqItem = {
  id: string;
  question: string;
  answer: string;
};

export type AceVisual = {
  gradient: string;
  glow: string;
  icon: LucideIcon;
};

export type AceInfo = {
  slug: string;
  name: string;
  shortDescription: string;
  about: string;
  imageAccent: string;
  visual: AceVisual;
  /** ID do power/ace no xat (catálogo oficial). */
  powerId?: number;
  /** Código do smilie exclusivo para assinantes (wiki). */
  smileyCode: string;
  /** Preço na loja xat, em xats. */
  storePriceXats: number;
  /** Duração padrão da assinatura em dias. */
  subscriptionDays: number;
  wikiUrl: string;
  scopes: AceScope[];
  hasLevels: boolean;
  requiresRenewal: boolean;
  isNew?: boolean;
  popularity: number;
  releasedAt: string;
  updatedAt: string;
  status: AceStatus;
  subscriptionLabel: string;
  renewalLabel: string;
  levelsLabel: string;
  availability: string;
  /** Quando definido, a disponibilidade no painel vira link para a loja xat. */
  availabilityLinkLabel?: string;
  features: AceFeature[];
  howItWorks: AceHowItWorksStep[];
  levels?: AceLevel[];
  faq: AceFaqItem[];
  /** Exibe a secção «Avatar frames» na página pública. */
  avatarFramesEnabled?: boolean;
  avatarFrames?: AceAvatarFrame[];
  /** Exibe a secção Pstyle com tema personalizado do CMS. */
  pstyleSectionEnabled?: boolean;
  pstyle?: PowerPstyleCmsConfig;
  /** Secção «Explore mais Aces» (título + até 4 aces manuais). */
  relatedExplore?: RelatedExploreConfig;
  /** Workflow de revisão — omitido = publicado (legado). */
  publicationStatus?: CmsPublicationStatus;
};

export type AceListingStats = {
  totalAces: number;
  latestAceName: string;
  levelsSystemLabel: string;
};

export const ACES_WIKI_URL = 'https://wiki.xat.com/Aces';

export const ACES_PAGE = {
  title: 'Aces',
  description:
    'Os Aces são assinaturas na loja xat que desbloqueiam recursos exclusivos para sua conta por um período determinado. Cada Ace inclui um smilie exclusivo para assinantes.',
  whatTitle: 'O que são os Aces?',
  whatText:
    'Ao contrário dos powers, os Aces funcionam como assinaturas pagas em xats. Eles permanecem ativos apenas durante o período contratado e exigem que você tenha days (assinatura de conta) para funcionar.',
  whatTextExtra:
    'Não podem ser trocados nem reembolsados, não entram no EveryPower e o preço pode variar conforme a procura. Alguns Aces oferecem descontos por nível com renovação automática a cada 30 dias.',
} as const;

/** FAQ geral da categoria Aces (wiki.xat.com/Aces). */
export const ACES_SHARED_FAQ: AceFaqItem[] = [
  {
    id: 'days-required',
    question: 'Preciso ter days para usar um Ace?',
    answer:
      'Sim. Para os Aces funcionarem, você precisa estar subscrito (ter days) na sua conta xat.',
  },
  {
    id: 'no-trade',
    question: 'Posso trocar ou pedir reembolso de um Ace?',
    answer:
      'Não. Aces não podem ser trocados com outros utilizadores nem reembolsados. Também não fazem parte do EveryPower.',
  },
  {
    id: 'gift-transfer',
    question: 'Posso oferecer ou transferir um Ace?',
    answer:
      'Sim. Pode oferecer um Ace novo a um amigo (custo e duração normais) ou transferir a sua subscrição ativa com o tempo restante. Aces oferecidos ou transferidos não podem ser anulados.',
  },
  {
    id: 'auto-renew',
    question: 'Como funciona a renovação automática?',
    answer:
      'Pode ativar a renovação automática a cada 30 dias. Em alguns Aces, cada renovação automática aumenta o seu nível e concede descontos em marcos. Nem todos os Aces têm sistema de níveis.',
  },
  {
    id: 'hat-ace',
    question: 'Existe um chapéu exclusivo para assinantes de Aces?',
    answer:
      'Subscrever pelo menos um Ace permite usar o chapéu (hat#A) ou (hat#hA), desde que tenha o power Hat. O código A substitui o ícone do peão; hA usa a cor do seu rank ou do power Pawn.',
  },
];

export function formatAceSubscriptionPrice(ace: Pick<AceInfo, 'storePriceXats' | 'subscriptionDays'>): string {
  return `${ace.storePriceXats} xats / ${ace.subscriptionDays} dias`;
}

const YELLOW_FAQ: AceFaqItem[] = [
  ...ACES_SHARED_FAQ,
  {
    id: 'yellow-pawn',
    question: 'O Yellow é um power?',
    answer:
      'Não. O peão amarelo não é um power e não pode ser trocado. O Yellow também não afeta o sistema de ranking da lista de visitantes.',
  },
  {
    id: 'yellow-everypower',
    question: 'Por que não vejo o peão amarelo com EveryPower ou Ruby?',
    answer:
      'Para ativar o peão amarelo padrão, deve desativar EveryPower e Ruby nas definições da conta.',
  },
  {
    id: 'sunburst-pstyle',
    question: 'Como ativo o tema Pstyle Sunburst?',
    answer:
      'Com a subscrição Yellow desbloqueia o tema Sunburst. Vá a Definições da conta → Pstyle e escolha "Sunburst". É necessário ter o power Pstyle.',
  },
];

export const MOCK_ACES: AceInfo[] = [
  {
    slug: 'yellow',
    name: 'Yellow',
    powerId: 700,
    shortDescription: 'Transforme o seu peão em amarelo e desbloqueie o tema Pstyle Sunburst.',
    about:
      'Yellow foi o primeiro Ace do xat. Permite tornar o peão amarelo e inclui o tema Pstyle Sunburst. Não é um power, não pode ser trocado e não altera o ranking na lista de visitantes. A subscrição é opcionalmente renovável a cada 30 dias.',
    imageAccent: '#eab308',
    visual: {
      gradient: 'linear-gradient(135deg, #fde047 0%, #eab308 45%, #a16207 100%)',
      glow: 'rgba(234, 179, 8, 0.4)',
      icon: Sun,
    },
    smileyCode: 'yellow',
    storePriceXats: 600,
    subscriptionDays: 30,
    wikiUrl: 'https://wiki.xat.com/Yellow',
    scopes: ['account'],
    hasLevels: false,
    requiresRenewal: true,
    popularity: 39,
    releasedAt: '2026-02-14',
    updatedAt: '2026-02-15',
    status: 'active',
    subscriptionLabel: 'Conta',
    renewalLabel: 'Opcional (30 dias)',
    levelsLabel: 'Sem níveis',
    availability: 'Loja xat',
    availabilityLinkLabel: 'Loja xat',
    features: [
      {
        id: 'yellow-pawn',
        title: 'Peão amarelo',
        description: 'Personalize o seu peão com a cor amarela enquanto a subscrição estiver ativa.',
      },
      {
        id: 'sunburst',
        title: 'Tema Pstyle Sunburst',
        description: 'Desbloqueia o gradiente Sunburst no diálogo de perfil (requer power Pstyle).',
      },
      {
        id: 'smiley',
        title: 'Smilie exclusivo',
        description: 'Acesso ao smilie (yellow), disponível apenas para assinantes deste Ace.',
      },
    ],
    howItWorks: [
      {
        step: 1,
        title: 'Subscreva na loja xat',
        description:
          'Abra o cartão Yellow na loja, leia as regras e clique em Subscribe. Conclua o checkout com utilizador e palavra-passe.',
      },
      {
        step: 2,
        title: 'Confirme a subscrição',
        description:
          'Após subscrever, aparece um badge no canto superior esquerdo e a duração restante no cartão do Ace.',
      },
      {
        step: 3,
        title: 'Ative o peão amarelo',
        description:
          'Desative EveryPower e Ruby se necessário para ver o peão amarelo padrão nas salas.',
      },
      {
        step: 4,
        title: 'Configure o Pstyle Sunburst',
        description:
          'Em Definições da conta → Pstyle, escolha o tema Sunburst (com o power Pstyle ativo).',
      },
    ],
    faq: YELLOW_FAQ,
  },
  {
    slug: 'vaf',
    name: 'Vaf',
    powerId: 738,
    shortDescription: 'Molduras de avatar com tema Valentine — seis quadradas e uma redonda.',
    about:
      'O Ace Vaf introduz molduras de avatar temáticas de Valentine. Inclui sete opções de moldura, smilie exclusivo e peões limitados com chapéus especiais. Renovação opcional a cada 30 dias.',
    imageAccent: '#f43f5e',
    visual: {
      gradient: 'linear-gradient(135deg, #fda4af 0%, #f43f5e 50%, #be123c 100%)',
      glow: 'rgba(244, 63, 94, 0.35)',
      icon: Heart,
    },
    smileyCode: 'vaf',
    storePriceXats: 60,
    subscriptionDays: 30,
    wikiUrl: 'https://wiki.xat.com/Vaf',
    scopes: ['account'],
    hasLevels: false,
    requiresRenewal: true,
    isNew: true,
    popularity: 36,
    releasedAt: '2026-04-19',
    updatedAt: '2026-03-02',
    status: 'active',
    subscriptionLabel: 'Conta',
    renewalLabel: 'Opcional (30 dias)',
    levelsLabel: 'Sem níveis',
    availability: 'Loja xat',
    availabilityLinkLabel: 'Loja xat',
    features: [
      {
        id: 'frames',
        title: '7 molduras Valentine',
        description:
          'HeartAurea (redonda), HeartBloom, HeartCloud, HeartGarden, HeartLocked, HeartPunk e HeartThorns (seis quadradas + uma redonda).',
      },
      {
        id: 'avatar',
        title: 'Molduras no avatar',
        description: 'Aplique molduras em Definições → Avatar, com pré-visualização antes de guardar.',
      },
      {
        id: 'limited-pawns',
        title: 'Peões limitados',
        description: 'Chapéus exclusivos (hat#hq, hat#ht, hat#he, hat#hf, hat#hi) para assinantes.',
      },
      {
        id: 'smiley',
        title: 'Smilie (vaf)',
        description: 'Smilie exclusivo para assinantes do Vaf.',
      },
    ],
    howItWorks: [
      {
        step: 1,
        title: 'Subscreva o Vaf',
        description: 'Adquira o Ace na loja xat por 60 xats (30 dias).',
      },
      {
        step: 2,
        title: 'Abra Definições → Avatar',
        description: 'Clique no seu nome e aceda ao menu Avatar nas definições.',
      },
      {
        step: 3,
        title: 'Escolha a moldura',
        description: 'Use o menu Frame, pré-visualize e clique em Save Avatar.',
      },
    ],
    faq: [
      ...ACES_SHARED_FAQ,
      {
        id: 'vaf-frames',
        question: 'Quantas molduras o Vaf inclui?',
        answer:
          'Sete molduras: uma redonda (HeartAurea) e seis quadradas (HeartBloom, HeartCloud, HeartGarden, HeartLocked, HeartPunk, HeartThorns).',
      },
    ],
  },
  {
    slug: 'xaf',
    name: 'Xaf',
    powerId: 736,
    shortDescription: 'Molduras de avatar sazonais — edição inicial com temas de Natal.',
    about:
      'O Ace Xaf permite decorar o avatar com molduras temáticas sazonais. A primeira edição inclui duas molduras quadradas de Natal; mais molduras podem ser adicionadas no futuro. Renovação opcional a cada 30 dias.',
    imageAccent: '#38bdf8',
    visual: {
      gradient: 'linear-gradient(135deg, #bae6fd 0%, #38bdf8 50%, #0369a1 100%)',
      glow: 'rgba(56, 189, 248, 0.35)',
      icon: Snowflake,
    },
    smileyCode: 'xaf',
    storePriceXats: 20,
    subscriptionDays: 30,
    wikiUrl: 'https://wiki.xat.com/Xaf',
    scopes: ['account'],
    hasLevels: false,
    requiresRenewal: true,
    popularity: 36,
    releasedAt: '2026-03-15',
    updatedAt: '2026-02-15',
    status: 'active',
    subscriptionLabel: 'Conta',
    renewalLabel: 'Opcional (30 dias)',
    levelsLabel: 'Sem níveis',
    availability: 'Loja xat',
    availabilityLinkLabel: 'Loja xat',
    features: [
      {
        id: 'christmas',
        title: 'Moldura Christmas',
        description: 'Moldura quadrada com tema natalício para o avatar.',
      },
      {
        id: 'frost',
        title: 'Moldura Frost',
        description: 'Moldura quadrada com visual gelado/neve.',
      },
      {
        id: 'seasonal',
        title: 'Mais molduras no futuro',
        description: 'Novas molduras sazonais podem ser adicionadas em atualizações.',
      },
      {
        id: 'smiley',
        title: 'Smilie (xaf)',
        description: 'Smilie exclusivo para assinantes do Xaf.',
      },
    ],
    howItWorks: [
      {
        step: 1,
        title: 'Subscreva o Xaf',
        description: 'Adquira na loja xat por 20 xats (30 dias).',
      },
      {
        step: 2,
        title: 'Definições → Avatar',
        description: 'Selecione a moldura no menu Frame.',
      },
      {
        step: 3,
        title: 'Pré-visualize e guarde',
        description: 'Clique em Preview e depois Save Avatar para aplicar.',
      },
    ],
    faq: ACES_SHARED_FAQ,
  },
  {
    slug: 'pawnhue',
    name: 'Pawnhue',
    powerId: 728,
    shortDescription: 'Altere a matiz do peão existente — coleções, chapéus e peões especiais.',
    about:
      'Pawnhue permite mudar a matiz (hue) da cor do peão que já possui, criando tons personalizados. Funciona em coleções, chapéus e peões especiais como EveryPower. Não concede powers de peão por si só — requer pelo menos um power de peão (Pink, Blueman, Purple, Gold, Ruby) ou peão de Supporter/Celebrity.',
    imageAccent: '#a855f7',
    visual: {
      gradient: 'linear-gradient(135deg, #d8b4fe 0%, #a855f7 50%, #6b21a8 100%)',
      glow: 'rgba(168, 85, 247, 0.35)',
      icon: Pipette,
    },
    smileyCode: 'pawnhue',
    storePriceXats: 300,
    subscriptionDays: 30,
    wikiUrl: 'https://wiki.xat.com/Pawnhue',
    scopes: ['account'],
    hasLevels: false,
    requiresRenewal: true,
    popularity: 37,
    releasedAt: '2025-12-14',
    updatedAt: '2025-07-19',
    status: 'active',
    subscriptionLabel: 'Conta',
    renewalLabel: 'Opcional (30 dias)',
    levelsLabel: 'Sem níveis',
    availability: 'Loja xat',
    availabilityLinkLabel: 'Loja xat',
    features: [
      {
        id: 'hue-slider',
        title: 'Slider de matiz',
        description: 'Escolha a matiz desejada nas Definições → Aces com pré-visualização.',
      },
      {
        id: 'collections',
        title: 'Compatível com coleções',
        description: 'Aplica-se a peões de coleção, chapéus e peões especiais.',
      },
      {
        id: 'requires-pawn',
        title: 'Requer peão existente',
        description: 'Necessita de um power de peão ou peão Supporter/Celebrity — não cria peões novos.',
      },
      {
        id: 'smiley',
        title: 'Smilie (pawnhue)',
        description: 'Smilie exclusivo para assinantes do Pawnhue.',
      },
    ],
    howItWorks: [
      {
        step: 1,
        title: 'Subscreva o Pawnhue',
        description: 'Adquira na loja xat por 300 xats (30 dias).',
      },
      {
        step: 2,
        title: 'Definições → Aces',
        description: 'Use o slider para escolher a matiz e clique em Preview.',
      },
      {
        step: 3,
        title: 'Guarde ou repor',
        description: 'Clique em Save Aces para aplicar ou Default para repor a cor original.',
      },
    ],
    faq: [
      ...ACES_SHARED_FAQ,
      {
        id: 'pawn-required',
        question: 'Preciso de um power de peão?',
        answer:
          'Sim. É necessário ter pelo menos um power de peão (Pink, Blueman, Purple, Gold ou Ruby) ou peão de Supporter/Celebrity. O Pawnhue apenas altera a matiz — não concede peões.',
      },
    ],
  },
  {
    slug: 'iconcolor',
    name: 'Iconcolor',
    powerId: 702,
    shortDescription: 'Personalize a cor da estrela, coração ou cruz no seu peão.',
    about:
      'Iconcolor permite alterar a cor dos ícones de estrela, coração e cruz no peão para qualquer cor. A cor afeta apenas o ícone individual, não o coração/cruz ligado de pares casados ou BFFs. Subhide e Reghide devem estar desativados para o ícone aparecer.',
    imageAccent: '#ec4899',
    visual: {
      gradient: 'linear-gradient(135deg, #f9a8d4 0%, #ec4899 50%, #9d174d 100%)',
      glow: 'rgba(236, 72, 153, 0.35)',
      icon: Palette,
    },
    smileyCode: 'iconcolor',
    storePriceXats: 150,
    subscriptionDays: 30,
    wikiUrl: 'https://wiki.xat.com/Iconcolor',
    scopes: ['account'],
    hasLevels: false,
    requiresRenewal: true,
    popularity: 39,
    releasedAt: '2024-08-31',
    updatedAt: '2026-04-27',
    status: 'active',
    subscriptionLabel: 'Conta',
    renewalLabel: 'Opcional (30 dias)',
    levelsLabel: 'Sem níveis',
    availability: 'Loja xat',
    availabilityLinkLabel: 'Loja xat',
    features: [
      {
        id: 'icon-colors',
        title: 'Cor dos ícones',
        description: 'Personalize estrela, coração ou cruz no peão com seletor de cor ou código hex.',
      },
      {
        id: 'pairs',
        title: 'Pares casados/BFF',
        description: 'Não altera o coração/cruz ligado entre parceiros — apenas o seu ícone.',
      },
      {
        id: 'visibility',
        title: 'Subhide / Reghide',
        description: 'Estes powers devem estar desativados para o ícone colorido aparecer.',
      },
      {
        id: 'smiley',
        title: 'Smilie (iconcolor)',
        description: 'Smilie exclusivo para assinantes do Iconcolor.',
      },
    ],
    howItWorks: [
      {
        step: 1,
        title: 'Subscreva o Iconcolor',
        description: 'Adquira na loja xat por 150 xats (30 dias).',
      },
      {
        step: 2,
        title: 'Definições → Aces',
        description: 'Use o seletor de cor ou introduza um código hex no campo Iconcolor.',
      },
      {
        step: 3,
        title: 'Guarde as alterações',
        description: 'Clique em Save Aces para aplicar a cor ao ícone do peão.',
      },
    ],
    faq: [
      ...ACES_SHARED_FAQ,
      {
        id: 'subhide',
        question: 'Por que o ícone não aparece?',
        answer:
          'Subhide e Reghide devem estar desativados. Além disso, a cor não afeta o ícone ligado de pares casados ou BFFs.',
      },
    ],
  },
];

export function getAceBySlug(slug: string): AceInfo | undefined {
  return MOCK_ACES.find((a) => a.slug === slug.toLowerCase());
}

export function getAceListingStats(): AceListingStats {
  return computeAceListingStats(MOCK_ACES);
}

export function computeAceListingStats(aces: AceInfo[]): AceListingStats {
  const sorted = [...aces].sort(
    (a, b) => new Date(b.releasedAt).getTime() - new Date(a.releasedAt).getTime()
  );

  return {
    totalAces: aces.length,
    latestAceName: sorted[0]?.name ?? '—',
    levelsSystemLabel: 'Alguns com descontos',
  };
}

export function getAceBadges(ace: AceInfo): AceBadgeId[] {
  const badges: AceBadgeId[] = [];
  if (ace.scopes.includes('account')) badges.push('conta');
  if (ace.scopes.includes('chat')) badges.push('chat');
  if (ace.hasLevels) badges.push('niveis');
  if (ace.requiresRenewal) badges.push('renovacao');
  if (ace.isNew) badges.push('novo');
  return badges;
}

export function getAceStatusLabel(status: AceStatus): string {
  switch (status) {
    case 'active':
      return 'Ativo';
    case 'legacy':
      return 'Legado';
    case 'upcoming':
      return 'Em breve';
  }
}

export function filterAndSortAces(
  aces: AceInfo[],
  options: {
    query: string;
    filter: AceFilterId;
    sort: AceSortId;
  }
): AceInfo[] {
  const q = options.query.trim().toLowerCase();
  let result = [...aces];

  if (q) {
    result = result.filter(
      (a) =>
        a.name.toLowerCase().includes(q) ||
        a.shortDescription.toLowerCase().includes(q) ||
        a.slug.toLowerCase().includes(q)
    );
  }

  switch (options.filter) {
    case 'account':
      result = result.filter((a) => a.scopes.includes('account'));
      break;
    case 'chat':
      result = result.filter((a) => a.scopes.includes('chat'));
      break;
    case 'with-levels':
      result = result.filter((a) => a.hasLevels);
      break;
    case 'without-levels':
      result = result.filter((a) => !a.hasLevels);
      break;
    default:
      break;
  }

  switch (options.sort) {
    case 'name':
      result.sort((a, b) => a.name.localeCompare(b.name, 'pt'));
      break;
    case 'popularity':
      result.sort((a, b) => b.popularity - a.popularity);
      break;
    case 'recent':
    default:
      result.sort(
        (a, b) => new Date(b.releasedAt).getTime() - new Date(a.releasedAt).getTime()
      );
      break;
  }

  return result;
}

/** Sugestões para explorar mais aces relacionados. */
export function getRelatedAces(current: AceInfo, count = 4, catalog?: AceInfo[]): AceInfo[] {
  const source = catalog?.length ? catalog : MOCK_ACES;
  const manual = resolveRelatedByItems(
    current.relatedExplore?.items,
    source,
    current.slug,
    count
  );
  if (manual) return manual;

  return [...source]
    .filter((a) => a.slug.toLowerCase() !== current.slug.toLowerCase())
    .sort((a, b) => b.popularity - a.popularity || a.name.localeCompare(b.name))
    .slice(0, count);
}

export function getRelatedAcesTitle(ace: AceInfo): string {
  const custom = ace.relatedExplore?.title?.trim();
  if (custom) return custom;
  return 'Explore mais Aces';
}

/** Preparado para futura API — hoje devolve mock local. */
export async function getAces(): Promise<AceInfo[]> {
  return MOCK_ACES;
}

export async function getAce(slug: string): Promise<AceInfo | undefined> {
  return getAceBySlug(slug);
}
